import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { BRAND } from "@/components/site/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  "Wealth Creation",
  "Retirement Planning",
  "Child's Education",
  "Tax Saving (ELSS)",
  "Buying a Home",
  "Just Exploring",
];

const emptyForm = { name: "", email: "", phone: "", goal: "", amount: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! Your enquiry is received. I'll reach out shortly.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full bg-transparent border-b border-black/15 focus:border-[#0e5b45] outline-none py-3 text-[#1a1a17] placeholder:text-stone-400 transition-colors duration-300";

  return (
    <section id="contact" className="bg-white py-24 md:py-36 border-t border-black/10" data-testid="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        {/* Left: details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="overline mb-5">Let&apos;s Talk</p>
          <h2 className="font-serif text-4xl md:text-6xl text-[#1a1a17] tracking-tight leading-[1.05]">
            Begin your <span className="italic text-[#0e5b45]">wealth</span> journey
          </h2>
          <p className="text-stone-600 mt-6 md:text-lg max-w-md leading-relaxed">
            A no-obligation conversation about your goals. Reach out and let&apos;s build a plan that fits your life.
          </p>

          <div className="mt-12 space-y-5">
            <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="group flex items-center gap-4 text-[#1a1a17] hover:text-[#0e5b45] transition-colors">
              <span className="w-11 h-11 rounded-full border border-black/15 grid place-items-center group-hover:border-[#0e5b45] transition-colors">
                <MessageCircle size={18} />
              </span>
              <span>WhatsApp — {BRAND.phones[0]}</span>
            </a>
            {BRAND.phones.map((p) => (
              <a key={p} href={`tel:+91${p}`} data-testid={`contact-phone-${p}`}
                className="group flex items-center gap-4 text-[#1a1a17] hover:text-[#0e5b45] transition-colors">
                <span className="w-11 h-11 rounded-full border border-black/15 grid place-items-center group-hover:border-[#0e5b45] transition-colors">
                  <Phone size={18} />
                </span>
                <span>+91 {p}</span>
              </a>
            ))}
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email"
              className="group flex items-center gap-4 text-[#1a1a17] hover:text-[#0e5b45] transition-colors">
              <span className="w-11 h-11 rounded-full border border-black/15 grid place-items-center group-hover:border-[#0e5b45] transition-colors">
                <Mail size={18} />
              </span>
              <span>{BRAND.email}</span>
            </a>
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6 lg:col-start-7 space-y-7"
          data-testid="lead-form"
        >
          <div className="grid sm:grid-cols-2 gap-7">
            <input className={inputBase} placeholder="Full Name *" value={form.name}
              onChange={(e) => update("name", e.target.value)} data-testid="input-name" />
            <input className={inputBase} type="tel" placeholder="Phone *" value={form.phone}
              onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" />
          </div>
          <input className={inputBase} type="email" placeholder="Email *" value={form.email}
            onChange={(e) => update("email", e.target.value)} data-testid="input-email" />

          <div className="grid sm:grid-cols-2 gap-7">
            <Select value={form.goal} onValueChange={(v) => update("goal", v)}>
              <SelectTrigger data-testid="input-goal"
                className="w-full bg-transparent border-0 border-b border-black/15 rounded-none focus:border-[#0e5b45] text-[#1a1a17] px-0 py-3 h-auto data-[placeholder]:text-stone-400">
                <SelectValue placeholder="Investment Goal" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/10 text-[#1a1a17]">
                {GOALS.map((g) => (
                  <SelectItem key={g} value={g} className="focus:bg-[#0e5b45] focus:text-white">{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input className={inputBase} placeholder="Monthly amount (₹)" value={form.amount}
              onChange={(e) => update("amount", e.target.value)} data-testid="input-amount" />
          </div>

          <textarea className={`${inputBase} resize-none`} rows={3} placeholder="Tell me a little about your goals (optional)"
            value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="input-message" />

          <button type="submit" disabled={loading} data-testid="lead-submit"
            className="group inline-flex items-center gap-3 bg-[#0e5b45] text-white px-8 py-4 rounded-full font-medium hover:bg-[#0b4a38] transition-colors duration-300 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
            {loading ? "Sending..." : "Send Enquiry"}
            {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
